'use strict';

import { SEARCH_IMG_QUERY_PARAMS, SIGNAL_IS_ABORTED_CODE } from '@core/constants/API';
import { SEARCHER_OPTIONS, SEARCHER_TYPES } from '@core/constants/SEARCHER';
import {
    CAT_CR_REP_BASE,
    MAPPING_ROUTES_REPORTS,
    REPORT_TYPE,
} from '@core/constants/REPORTS';
import { SEARCH_REPORTS_RESULT_EVENT } from '@core/constants/EVENTS';
import { NOTIFICATIONS_TYPE } from '@core/constants/NOTIFICATIONS';
import { getReportList } from '@core/utils/api';
import { showNotification } from '@core/utils/notification-messages';
import { delayTranslate, translate } from '@core/i18n/window-language';
import { updateCurrentPageWhenEmptyResults } from '@core/utils/history-state';
import { removePrefixFilter } from '@core/utils/filter';
import { relaunchQuery401or403status, updateMetaTags } from '@core/utils/utils';
import { checkValidAccessToken } from '@core/utils/oauth';
import { addStoreFiltersState } from '@core/utils/store';
import { ROUTES } from '@core/constants/ROUTES';
import { generateCode } from '@core/utils/uuid';
import { getLastReportType } from '@core/utils/report';
import { COMPANY_SHORT_NAME } from '@core/constants/COMPANY';

/**
 * Set the canonical tag for the page
 * @returns {void}
 * @private
 */
const _setCanonicalTag = () => {
    let canonicalLink = document.querySelector("link[rel='canonical']");
    if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', window.location.href);
};

/**
 * Get all the reports
 * @param {Array} results - Array of reports
 * @returns {{
 *   title: string,
 *   type: string,
 *   description: string,
 *   content: Array
 * }} - All the reports
 * @private
 */
const _getAllReports = (results) => {
    /** @type {Array<object>} */
    const allReports = results.reduce((acc, curr) => {
        return [...acc, ...curr.content];
    }, []);

    return allReports;
};

/**
 * Update the title of the page based on current report type
 * @returns {void}
 */
export const updateTitle = () => {
    const /** @type {URLSearchParams} */ urlParams = new URLSearchParams(
            window.location.search,
        );

    let title, description;

    if (!urlParams.has(SEARCHER_TYPES.REPORT_TYPE)) {
        title = translate('metaTags.reportsList.default.title');
        description = translate('metaTags.reportsList.default.description');

        updateMetaTags({
            title: title.replace('%company', COMPANY_SHORT_NAME),
            description: description.replace('%company', COMPANY_SHORT_NAME),
        });

        return;
    }

    const /** @type {Boolean} */ isEdVideo =
            urlParams.has(SEARCHER_TYPES.OPTION) &&
            urlParams.get(SEARCHER_TYPES.OPTION) === SEARCHER_OPTIONS.ED_VIDEOS;

    const /** @type {Boolean} */ isReportVideo =
            urlParams.get(SEARCHER_TYPES.REPORT_TYPE) === REPORT_TYPE.ED_LAST_VIDEOS;

    const /** @type {Boolean} */ isVideoTab = isEdVideo && isReportVideo;

    const /** @type {Boolean} */ mostViewed =
            urlParams.get(SEARCHER_TYPES.REPORT_TYPE) === REPORT_TYPE.ED_MOST_VIEWED;

    const /** @type {Boolean} */ isEdFamous =
            urlParams.get(SEARCHER_TYPES.REPORT_TYPE) === REPORT_TYPE.ED_FAMOUS;

    const /** @type {Boolean} */ isEdRoyal =
            urlParams.get(SEARCHER_TYPES.REPORT_TYPE) === REPORT_TYPE.ED_ROYAL;

    const /** @type {Boolean} */ isEdPerformance =
            urlParams.get(SEARCHER_TYPES.REPORT_TYPE) === REPORT_TYPE.ED_PERFORMANCE;

    const /** @type {Boolean} */ isEdPolitics =
            urlParams.get(SEARCHER_TYPES.REPORT_TYPE) === REPORT_TYPE.ED_POLITICS;

    const /** @type {Boolean} */ isEdSports =
            urlParams.get(SEARCHER_TYPES.REPORT_TYPE) === REPORT_TYPE.ED_SPORTS;

    const /** @type {Boolean} */ isEdFashion =
            urlParams.get(SEARCHER_TYPES.REPORT_TYPE) === REPORT_TYPE.ED_FASHION;

    const /** @type {Boolean} */ isEdSociety =
            urlParams.get(SEARCHER_TYPES.REPORT_TYPE) === REPORT_TYPE.ED_SOCIETY;

    const /** @type {Boolean} */ isCrNourishment =
            urlParams.get(SEARCHER_TYPES.REPORT_TYPE) === REPORT_TYPE.CR_NOURISHMENT;

    const /** @type {Boolean} */ isCrBeauty =
            urlParams.get(SEARCHER_TYPES.REPORT_TYPE) === REPORT_TYPE.CR_BEAUTY;

    const /** @type {Boolean} */ isCrDecoration =
            urlParams.get(SEARCHER_TYPES.REPORT_TYPE) === REPORT_TYPE.CR_DECORATION;

    const /** @type {Boolean} */ isCrNature =
            urlParams.get(SEARCHER_TYPES.REPORT_TYPE) === REPORT_TYPE.CR_NATURE;

    const /** @type {Boolean} */ isCrTravel =
            urlParams.get(SEARCHER_TYPES.REPORT_TYPE) === REPORT_TYPE.CR_TRAVEL;

    switch (true) {
        case isVideoTab:
            title = translate('metaTags.reportsList.video.title');
            description = translate('metaTags.reportsList.video.description');
            break;
        case mostViewed:
            title = translate('metaTags.reportsList.mostViewed.title');
            description = translate('metaTags.reportsList.mostViewed.description');
            break;
        case isEdFamous:
            title = translate('metaTags.reportsList.famous.title');
            description = translate('metaTags.reportsList.famous.description');
            break;
        case isEdRoyal:
            title = translate('metaTags.reportsList.royal.title');
            description = translate('metaTags.reportsList.royal.description');
            break;
        case isEdPerformance:
            title = translate('metaTags.reportsList.performance.title');
            description = translate('metaTags.reportsList.performance.description');
            break;
        case isEdPolitics:
            title = translate('metaTags.reportsList.politics.title');
            description = translate('metaTags.reportsList.politics.description');
            break;
        case isEdSports:
            title = translate('metaTags.reportsList.sports.title');
            description = translate('metaTags.reportsList.sports.description');
            break;
        case isEdFashion:
            title = translate('metaTags.reportsList.fashion.title');
            description = translate('metaTags.reportsList.fashion.description');
            break;
        case isEdSociety:
            title = translate('metaTags.reportsList.society.title');
            description = translate('metaTags.reportsList.society.description');
            break;
        case isCrNourishment:
            title = translate('metaTags.reportsList.nourishment.title');
            description = translate('metaTags.reportsList.nourishment.description');
            break;
        case isCrBeauty:
            title = translate('metaTags.reportsList.beauty.title');
            description = translate('metaTags.reportsList.beauty.description');
            break;
        case isCrDecoration:
            title = translate('metaTags.reportsList.decoration.title');
            description = translate('metaTags.reportsList.decoration.description');
            break;
        case isCrNature:
            title = translate('metaTags.reportsList.nature.title');
            description = translate('metaTags.reportsList.nature.description');
            break;
        case isCrTravel:
            title = translate('metaTags.reportsList.travel.title');
            description = translate('metaTags.reportsList.travel.description');
            break;
        default:
            title = translate('metaTags.reportsList.default.title');
            description = translate('metaTags.reportsList.default.description');
            break;
    }

    _setCanonicalTag();
    updateMetaTags({
        title: title.replace('%company', COMPANY_SHORT_NAME),
        description: description.replace('%company', COMPANY_SHORT_NAME),
    });
};

/**
 * Redirect to the reports list page.
 * @returns {void}
 */
export const redirectTo = () => {
    const /** @type {string} */ currentPath = window.location.pathname;

    const /** @type {Boolean} */ isFriendlyUrl =
            currentPath.includes(ROUTES.REPORTS_LIST.replace('index.html', '')) &&
            !currentPath.includes(ROUTES.REPORTS_LIST);

    if (!isFriendlyUrl) return;

    const /** @type {string|undefined} */ reportType = window.location.pathname
            .split('/')
            .pop();

    if (!reportType) return;

    const /** @type {string|undefined} */ newReportType =
            MAPPING_ROUTES_REPORTS[reportType.replace('-', '_').toUpperCase()];

    if (!newReportType) return;

    const /** @type {string} */ searchIdCode = generateCode();
    addStoreFiltersState(searchIdCode);

    const /** @type {Boolean} */ isCatCrRepBase = newReportType.includes(CAT_CR_REP_BASE);

    let searchOption = isCatCrRepBase
        ? SEARCHER_OPTIONS.CR_IMGS
        : SEARCHER_OPTIONS.ED_IMGS;

    searchOption =
        newReportType === REPORT_TYPE.ED_LAST_VIDEOS
            ? SEARCHER_OPTIONS.ED_VIDEOS
            : searchOption;

    const /** @type {Boolean} */ aiValue = isCatCrRepBase;

    const /** @type {string} */ newPath = `${window.location.origin}${ROUTES.REPORTS_LIST}?${SEARCHER_TYPES.REPORT_TYPE}=${newReportType}&${SEARCHER_TYPES.OPTION}=${searchOption}&${SEARCHER_TYPES.AI}=${aiValue}&${SEARCHER_TYPES.SEARCHER_ID}=${searchIdCode}`;

    // remove the current path from the history
    window.history.replaceState({}, document.title, newPath);

    window.location.href = newPath;
};

/**
 * Search the reports with the query params
 * @param {Object} params - The options for the search.
 * @param {Object} params.searchImgQuery - The query params for the search.
 * @param {number} [params.times=0] - Number of retry attempts
 * @returns {Promise<void>}
 */
export const searchReports = async ({ searchImgQuery, times = 0 } = {}) => {
    /* Get all the cat filters */
    /** @type {string} */
    const catFilter = getLastReportType(searchImgQuery[SEARCH_IMG_QUERY_PARAMS.FILTERS]);

    await checkValidAccessToken();

    try {
        const response = await getReportList(Object.assign({}, searchImgQuery));

        if (!response.ok) {
            const wasRelaunched = await relaunchQuery401or403status(
                response,
                { times, searchImgQuery },
                searchReports,
            );

            if (wasRelaunched) {
                return;
            }

            return;
        }

        /** @type {Array<object>} */
        const responseJson = await response.json();
        let /** @type {Array<object>} */ reports = responseJson || [];

        if (!catFilter && reports) {
            reports = _getAllReports(reports.result);
        }

        if (!reports.length && searchImgQuery[SEARCH_IMG_QUERY_PARAMS.PAGE_AI]) {
            updateCurrentPageWhenEmptyResults(
                searchImgQuery[SEARCH_IMG_QUERY_PARAMS.PAGE_AI],
            );

            return;
        }

        reports = reports.map((report) => {
            return {
                ...report,
                defaultItem: {
                    ...report.defaultItem,
                    current_page: searchImgQuery[SEARCH_IMG_QUERY_PARAMS.PAGE_AI],
                    ...(report.title && { title_event: report.title }),
                    ...(report.elements && { elements_event: report.elements }),
                },
            };
        });

        document.dispatchEvent(
            new CustomEvent(SEARCH_REPORTS_RESULT_EVENT, {
                detail: {
                    reports: reports,
                    page: searchImgQuery[SEARCH_IMG_QUERY_PARAMS.PAGE_AI],
                    reportFilter: catFilter && removePrefixFilter(catFilter),
                },
            }),
        );
    } catch (error) {
        /* If the error is because the signal is aborted, we don't show the notification */
        if (error?.message?.includes(SIGNAL_IS_ABORTED_CODE)) {
            return;
        }

        console.error('Error searching the images', error);
        delayTranslate(() => {
            showNotification(
                translate('notification.errors.searchQueryError'),
                NOTIFICATIONS_TYPE.ERROR,
            );
        });

        document.dispatchEvent(
            new CustomEvent(SEARCH_REPORTS_RESULT_EVENT, {
                detail: {
                    reports: [],
                    page: searchImgQuery[SEARCH_IMG_QUERY_PARAMS.PAGE_AI],
                    reportFilter: catFilter && removePrefixFilter(catFilter),
                },
            }),
        );
    }
};

/**
 * Load the previous reports.
 * @returns {Promise<void>}
 */
export const loadPreviousReports = async ({ searchImgQuery }) => {
    /* Get all the cat filters */
    /** @type {string} */
    const catFilter = getLastReportType(searchImgQuery[SEARCH_IMG_QUERY_PARAMS.FILTERS]);

    await checkValidAccessToken();

    const /** @type {object} */ searchReportQueryState = Object.assign(
            {},
            searchImgQuery,
        );

    const /** @type {Number} */ firstLoadPreviousPage =
            searchImgQuery[SEARCH_IMG_QUERY_PARAMS.LOAD_PREVIOUS_PAGE];
    const /** @type {Number} */ lastPage =
            searchImgQuery[SEARCH_IMG_QUERY_PARAMS.LOAD_PREVIOUS_LAST_PAGE];

    const /** @type {Promise[]} */ allPromises = [];

    for (let i = firstLoadPreviousPage; i < lastPage; i++) {
        searchReportQueryState[SEARCH_IMG_QUERY_PARAMS.PAGE_AI] = i;

        allPromises.push(
            getReportList(Object.assign({}, searchReportQueryState))
                .then(async (response) => {
                    if (!response.ok) {
                        throw Error('Error searching the image');
                    }

                    /** @type {Array<object>} */
                    const responseJson = await response.json();
                    let /** @type {Array<object>} */ reports = responseJson || [];

                    if (!catFilter && reports) {
                        reports = _getAllReports(reports.result);
                    }

                    reports = reports.map((report) => {
                        return {
                            ...report,
                            defaultItem: {
                                ...report.defaultItem,
                                current_page: i,
                            },
                        };
                    });

                    return reports;
                })
                .catch((error) => {
                    /* If the error is because the signal is aborted, we don't show the notification */
                    if (error?.message?.includes(SIGNAL_IS_ABORTED_CODE)) {
                        return;
                    }

                    console.error('Error searching the images', error);
                    delayTranslate(() => {
                        showNotification(
                            translate('notification.errors.searchQueryError'),
                            NOTIFICATIONS_TYPE.ERROR,
                        );
                    });

                    return [];
                }),
        );
    }

    Promise.all(allPromises)
        .then((reports) => {
            document.dispatchEvent(
                new CustomEvent(SEARCH_REPORTS_RESULT_EVENT, {
                    detail: {
                        reports: reports.flat(),
                        page: searchImgQuery[SEARCH_IMG_QUERY_PARAMS.PAGE_AI],
                        reportFilter: catFilter && removePrefixFilter(catFilter),
                        loadPrevious: true,
                    },
                }),
            );
        })
        .catch((error) => {
            console.error('Error searching the previous reports', error);
        })
        .finally(() => {
            searchImgQuery[SEARCH_IMG_QUERY_PARAMS.LOAD_PREVIOUS] = false;
        });
};
